import { LogManager } from '@src/utility/Logger';
import { makeAutoObservable } from 'mobx';

import type { RootStore } from './RootStore';
import type { Logger } from '@src/utility/Logger';

/**
 * LayoutStore manages the layout-related state of the application.
 */
export class LayoutStore {
  // Core state
  timelineHeight: string = '30%';
  transcriptWidth: string = '380px';
  isDragging: boolean = false;
  isSidebarExpanded: boolean = false;

  /**
   * Stage width below which one line of transport controls (~570px measured)
   * no longer fits under the canvas; opening a right-rail panel on a narrow
   * window narrows the transcript to keep the stage at least this wide.
   */
  static readonly MIN_STAGE_WIDTH_WITH_SIDEBAR = 600;

  // Non-observable properties
  private _rootStore: RootStore;
  private _logger: Logger;
  transcriptMinWidth = 190;
  transcriptMaxWidth = 760;
  /** Transcript width to give back when the right panel closes, if we narrowed it. */
  private _transcriptWidthBeforeSidebar: number | null = null;

  constructor(rootStore: RootStore) {
    this._rootStore = rootStore;
    this._logger = LogManager.getLogger('layout-store');
    this.isSidebarExpanded = false;

    makeAutoObservable(this, {
      transcriptMinWidth: false,
      transcriptMaxWidth: false,
      _transcriptWidthBeforeSidebar: false,
    });
  }

  setTimelineHeight(newHeight: number) {
    this.timelineHeight = `${newHeight}%`;
  }

  setTranscriptWidth(newWidth: number) {
    this.transcriptWidth = `${newWidth}px`;
  }

  setIsDragging(isDragging: boolean) {
    this.isDragging = isDragging;
  }

  /**
   * A right-rail panel is open and `sharedWidth` is what the transcript and
   * stage split between them. If the stage would be too narrow for the canvas
   * and its transport row, take the transcript down to its minimum and
   * remember what to give back; once the remembered width fits again (the
   * window grew), give it back early.
   */
  fitTranscriptBesideSidebar(sharedWidth: number): void {
    const min = LayoutStore.MIN_STAGE_WIDTH_WITH_SIDEBAR;
    const remembered = this._transcriptWidthBeforeSidebar;
    if (remembered !== null) {
      if (sharedWidth - remembered >= min) this.restoreTranscriptAfterSidebar();
      return;
    }
    const current = parseFloat(this.transcriptWidth);
    if (sharedWidth - current >= min) return;
    if (!(current > this.transcriptMinWidth)) return;
    this._transcriptWidthBeforeSidebar = current;
    this.setTranscriptWidth(this.transcriptMinWidth);
  }

  /**
   * The right-rail panel closed. Give the transcript its width back — unless
   * the user resized it while the panel was open, in which case their width
   * stands.
   */
  restoreTranscriptAfterSidebar(): void {
    const remembered = this._transcriptWidthBeforeSidebar;
    this._transcriptWidthBeforeSidebar = null;
    if (remembered === null) return;
    if (parseFloat(this.transcriptWidth) !== this.transcriptMinWidth) return;
    this.setTranscriptWidth(remembered);
  }

  // Sidebar expansion state management

  /**
   * Initialize sidebar state when a video is loaded. Always starts collapsed —
   * the user must explicitly open the panel each session.
   */
  initializeSidebarForVideo(_videoId: string | null): void {
    this.isSidebarExpanded = false;
  }

  setIsSidebarExpanded(isExpanded: boolean): void {
    this.isSidebarExpanded = isExpanded;
  }

  toggleSidebarExpanded(): void {
    this.setIsSidebarExpanded(!this.isSidebarExpanded);
  }
}
